import {SupportProgramMeta, SupportProgramPatient} from "@/program/interfaces/Program";
import {FollowUpEvent, FollowUpEventKind, ProgramTimelineEvent} from "@/program/interfaces/ProgramFollowUp";
import {RcsPayload} from "@/notification-template/interfaces";
import {ConversationMessage} from "@/conversation/interfaces";

/**
 * The legend label of each event kind.
 */
export const FOLLOW_UP_EVENT_LABELS: Record<FollowUpEventKind, string> = {
    interview: 'Entretien pharmaceutique',
    survey: 'Questionnaire',
    rcs: 'Message envoyé',
    rcs_reply: 'Réponse patient',
    validating_sale: 'Vente validante',
    targeting_sale: 'Vente de ciblage',
    appointment: 'Rendez-vous',
    mssante: 'Mail MSSanté',
};

/**
 * The rank of each event kind when several share a marker, the lowest drawn:
 * the rare events that matter come before the sales, which happen all the time.
 */
export const FOLLOW_UP_EVENT_PRIORITY: Record<FollowUpEventKind, number> = {
    validating_sale: 0,
    appointment: 1,
    interview: 2,
    survey: 3,
    mssante: 4,
    rcs: 4,
    rcs_reply: 4,
    targeting_sale: 5,
};

/**
 * A group of events close enough on the axis to be drawn as a single marker.
 */
export interface FollowUpCluster {
    id: string;
    /** The position on the axis, from 0 to 1. */
    position: number;
    events: FollowUpEvent[];
}

/**
 * Build the follow-up events of a consumer from its program sales and its HDS timeline.
 *
 * @param sales - The consumer program groups with their sales, if loaded.
 * @param timeline - The consumer HDS timeline events.
 * @param meta - The program meta, for the group labels.
 */
function events(
    sales: SupportProgramPatient | undefined,
    timeline: ProgramTimelineEvent[],
    meta: SupportProgramMeta
): FollowUpEvent[] {
    const now = Date.now();
    const saleEvents = (['included', 'excluded'] as const).flatMap(category => (sales?.groups[category] ?? [])
        .flatMap(group => (group.sales ?? []).map((sale, index): FollowUpEvent => ({
            id: `${category}-${group.slug}-${index}`,
            kind: category === 'included' ? 'targeting_sale' : 'validating_sale',
            date: new Date(sale.date),
            planned: false,
            title: meta[category][group.slug] ?? FOLLOW_UP_EVENT_LABELS[category === 'included' ? 'targeting_sale' : 'validating_sale'],
            detail: [sale.quantity && sale.quantity > 1 ? `${sale.quantity} ×` : null, sale.name].filter(Boolean).join(' '),
        }))));

    const timelineEvents = timeline.map((event): FollowUpEvent => {
        const date = new Date(event.date);

        switch (event.type) {
            case 'interview':
                return {
                    id: `interview-${event.id}`,
                    kind: 'interview',
                    date,
                    planned: false,
                    title: event.title ?? FOLLOW_UP_EVENT_LABELS.interview,
                    detail: event.ended_at ? 'Entretien terminé' : 'Entretien en cours',
                };
            case 'survey':
                return {
                    id: `survey-${event.steppable_type}-${event.id}`,
                    kind: 'survey',
                    date,
                    planned: false,
                    title: event.title ?? FOLLOW_UP_EVENT_LABELS.survey,
                };
            case 'rcs':
                return {
                    id: `rcs-${event.id}`,
                    kind: 'rcs',
                    date,
                    planned: false,
                    title: event.message.channel === 'sms' ? 'SMS envoyé' : 'RCS envoyé',
                    detail: messageText(event.message),
                    message: event.message,
                };
            case 'rcs_reply':
                return {
                    id: `reply-${event.id}`,
                    kind: 'rcs_reply',
                    date,
                    planned: false,
                    title: FOLLOW_UP_EVENT_LABELS.rcs_reply,
                    detail: event.text,
                };
            case 'rcs_planned':
                return {
                    id: `planned-${event.sequence_instance_id}-${event.id}`,
                    kind: 'rcs',
                    date,
                    planned: true,
                    title: event.title,
                    detail: event.sequence ? `Suivi automatique « ${event.sequence} »` : null,
                };
            case 'appointment':
                return {
                    id: `appointment-${event.id}`,
                    kind: 'appointment',
                    date,
                    planned: date.getTime() > now,
                    title: event.appointment_type ?? event.title ?? FOLLOW_UP_EVENT_LABELS.appointment,
                    detail: APPOINTMENT_STATUS_LABELS[event.status],
                };
            case 'mssante':
                return {
                    id: `mssante-${event.id}`,
                    kind: 'mssante',
                    date,
                    planned: false,
                    title: event.subject,
                    detail: [
                        event.audience === 'patient' ? 'À Mon espace santé du patient' : `À ${event.recipient}`,
                        event.attachments_count ? `${event.attachments_count} pièce${event.attachments_count > 1 ? 's' : ''} jointe${event.attachments_count > 1 ? 's' : ''}` : null,
                    ].filter(Boolean).join(' · '),
                };
        }
    });

    return [...saleEvents, ...timelineEvents]
        .filter(event => !isNaN(event.date.getTime()))
        .sort((a, b) => a.date.getTime() - b.date.getTime());
}

const APPOINTMENT_STATUS_LABELS: Record<string, string> = {
    pending: 'En attente de confirmation',
    confirmed: 'Confirmé',
    refused: 'Refusé',
    canceled: 'Annulé',
};

/**
 * The readable text of a sent message, for the tooltip.
 */
function messageText(message: ConversationMessage): string | null {
    if (message.channel === 'sms') {
        return (message.payload as { text?: string })?.text ?? null;
    }

    const payload = message.payload as RcsPayload;

    switch (payload?.type) {
        case 'text':
            return payload.content;
        case 'card':
            return payload.title || payload.content;
        case 'carousel':
            return payload.cards[0]?.title ?? null;
        default:
            return null;
    }
}

/**
 * Group the events of the axis window into markers, merging the events closer
 * than the given gap: a row then draws a bounded number of markers whatever
 * the number of events.
 *
 * @param list - The sorted events.
 * @param from - The start of the axis.
 * @param to - The end of the axis.
 * @param gap - The minimal gap between two markers, as a fraction of the axis.
 */
function clusters(list: FollowUpEvent[], from: Date, to: Date, gap: number): FollowUpCluster[] {
    const span = to.getTime() - from.getTime();
    const result: FollowUpCluster[] = [];

    for (const event of list) {
        const position = (event.date.getTime() - from.getTime()) / span;

        if (position < 0 || position > 1) {
            continue;
        }

        const last = result[result.length - 1];

        if (last && position - last.position < gap && last.events[0].planned === event.planned) {
            last.events.push(event);
        } else {
            result.push({id: event.id, position, events: [event]});
        }
    }

    return result;
}

export default {events, clusters};
